import React, {
  lazy,
  useCallback,
  useState,
  type ComponentProps,
  type ComponentType,
} from "react";
import {
  APP_BUILD_ID,
  fetchDeployedBuildId,
  isAppVersionCheckEnabled,
  isBuildObsolete,
  isPublishedBuildReady,
  markAutoUpdateAttempted,
  reloadToLatestBuild,
  wasAutoUpdateAttempted,
} from "./appVersion";
import { reportGlobalError } from "./globalErrorReporting";
import { neutralActionButtonClassName } from "./operations/formStyles";
import { PageStatus } from "./operations/PageStatus";
import { isProtectedWorkActive } from "./protectedWork";

/**
 * Destino alternativo oferecido quando a área não abre (ex.: o seletor de
 * aplicativos, que já está carregado e funciona sem conexão). Sem ele, um PWA
 * instalado ficaria sem saída até a conexão voltar.
 */
export const AreaFallbackNavigationContext = React.createContext<{
  label: string;
  onNavigate: () => void;
} | null>(null);

/** Falha ao baixar o código de uma área (chunk indisponível ou rede). */
export class AreaUnavailableError extends Error {
  constructor(readonly cause: unknown) {
    super(cause instanceof Error ? cause.message : String(cause));
    this.name = "AreaUnavailableError";
  }
}

export type LazyAreaComponent<T extends ComponentType<any>> = ComponentType<
  ComponentProps<T>
> & {
  /** Baixa o código da área sem renderizá-la; falhas são silenciosas. */
  preload: () => Promise<void>;
};

/**
 * `React.lazy` para as áreas do app.
 *
 * - Depois de um deploy, os chunks da build anterior deixam de existir: se a
 *   importação falhar e houver build nova publicada, aplica a mesma
 *   atualização automática de `useAppUpdateCoordinator` (uma tentativa por
 *   build, nunca com trabalho protegido ativo). Offline, `version.json` não
 *   responde e nada é recarregado.
 * - Qualquer outra falha de carregamento (ex.: sem conexão) mostra uma
 *   mensagem com a área e "Tentar novamente", e repete sozinha quando a
 *   conexão volta, sem recarregar a página.
 * - `preload()` reaproveita a mesma importação: o que foi baixado com conexão
 *   continua disponível offline durante a sessão.
 */
export function lazyArea<T extends ComponentType<any>>(
  load: () => Promise<{ default: T }>,
  label: string,
): LazyAreaComponent<T> {
  let pending: Promise<{ default: T }> | null = null;
  // Falha já exibida ao usuário: a próxima montagem tenta importar de novo.
  let failureShown = false;

  const importArea = () => {
    if (!pending) {
      pending = load().catch((error) => {
        // Permite nova tentativa: o webpack também refaz o pedido do chunk.
        pending = null;
        throw error;
      });
    }
    return pending;
  };

  const createLazy = () =>
    lazy(async () => {
      try {
        return await importArea();
      } catch (error) {
        if (await reloadIfBuildObsolete()) {
          // Mantém o fallback do Suspense até o navegador recarregar.
          return new Promise<{ default: T }>(() => undefined);
        }
        throw new AreaUnavailableError(error);
      }
    });

  let LazyArea = createLazy();

  const markFailureShown = () => {
    failureShown = true;
  };

  function Area(props: ComponentProps<T>) {
    useState(() => {
      // `React.lazy` guarda a rejeição; ao voltar para a área, tenta de novo.
      if (failureShown) {
        failureShown = false;
        LazyArea = createLazy();
      }
      return null;
    });
    const [attempt, setAttempt] = useState(0);
    const retry = useCallback(() => {
      failureShown = false;
      LazyArea = createLazy();
      setAttempt((current) => current + 1);
    }, []);

    return (
      <AreaLoadBoundary
        key={attempt}
        label={label}
        onRetry={retry}
        onFailureShown={markFailureShown}
      >
        <LazyArea {...props} />
      </AreaLoadBoundary>
    );
  }

  return Object.assign(Area, {
    preload: () => importArea().then(
      () => undefined,
      () => undefined,
    ),
  });
}

async function reloadIfBuildObsolete() {
  if (!isAppVersionCheckEnabled()) return false;
  const deployedBuildId = await fetchDeployedBuildId();
  if (
    !deployedBuildId ||
    !isBuildObsolete(APP_BUILD_ID, deployedBuildId) ||
    wasAutoUpdateAttempted(deployedBuildId) ||
    isProtectedWorkActive() ||
    // Publicação ainda incompleta: a área mostra a falha com nova tentativa.
    !(await isPublishedBuildReady())
  ) {
    return false;
  }
  markAutoUpdateAttempted(deployedBuildId);
  reloadToLatestBuild();
  return true;
}

function isOffline() {
  return typeof navigator !== "undefined" && navigator.onLine === false;
}

interface AreaLoadBoundaryProps {
  label: string;
  onRetry: () => void;
  onFailureShown: () => void;
  children: React.ReactNode;
}

interface AreaLoadBoundaryState {
  error: unknown;
}

/**
 * Trata somente falhas de carregamento da área. Erros de renderização da
 * própria área seguem para os boundaries de domínio e para o global.
 */
class AreaLoadBoundary extends React.Component<
  AreaLoadBoundaryProps,
  AreaLoadBoundaryState
> {
  static contextType = AreaFallbackNavigationContext;

  state: AreaLoadBoundaryState = { error: null };

  static getDerivedStateFromError(error: unknown): AreaLoadBoundaryState {
    return { error };
  }

  componentDidCatch(error: unknown) {
    if (!(error instanceof AreaUnavailableError) || isOffline()) return;
    // Com conexão, a falha não é esperada: preserva o diagnóstico.
    const cause = error.cause instanceof Error ? error.cause : error;
    reportGlobalError({
      type: "react",
      message: `Falha ao carregar a área ${this.props.label}: ${cause.message}`,
      stack: cause.stack ?? null,
    });
  }

  componentDidMount() {
    this.syncOnlineRetry();
  }

  componentDidUpdate() {
    this.syncOnlineRetry();
  }

  componentWillUnmount() {
    window.removeEventListener("online", this.props.onRetry);
  }

  /** Repete a importação quando a conexão volta, sem recarregar a página. */
  private syncOnlineRetry() {
    if (this.state.error instanceof AreaUnavailableError) {
      this.props.onFailureShown();
      window.addEventListener("online", this.props.onRetry);
    } else {
      window.removeEventListener("online", this.props.onRetry);
    }
  }

  render() {
    const { error } = this.state;
    if (!error) return this.props.children;
    if (!(error instanceof AreaUnavailableError)) throw error;
    const fallbackNavigation = this.context as React.ContextType<
      typeof AreaFallbackNavigationContext
    >;

    return (
      <div className="flex min-h-dvh items-center justify-center bg-surface font-sans">
        <PageStatus
          tone="error"
          title={`Não foi possível abrir ${this.props.label}.`}
          description={
            isOffline()
              ? "Você está sem conexão. A área abre automaticamente quando a conexão voltar."
              : "Verifique sua conexão e tente novamente."
          }
          actions={
            <>
              <button
                type="button"
                onClick={this.props.onRetry}
                className={neutralActionButtonClassName}
              >
                Tentar novamente
              </button>
              {fallbackNavigation ? (
                <button
                  type="button"
                  onClick={fallbackNavigation.onNavigate}
                  className={neutralActionButtonClassName}
                >
                  {fallbackNavigation.label}
                </button>
              ) : null}
            </>
          }
        />
      </div>
    );
  }
}
